import { FC } from "react";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { checkIsActive, KTIcon } from "../../../../helpers";

type Props = {
  to: string;
  title: string;
  description?: string;
  icon?: string;
  fontIcon?: string;
  hasArrow?: boolean;
  hasBullet?: boolean;
  iconOnly?: boolean;
  onClick?: () => void; // Optional click handler (used for create actions that open dialogs)
};

const MenuItem: FC<Props> = ({
  to,
  title,
  description,
  icon,
  fontIcon,
  hasArrow = false,
  hasBullet = false,
  iconOnly = false,
  onClick,
}) => {
  const { pathname } = useLocation();

  if (iconOnly && icon) {
    const renderTooltip = (props: any) => (
      <Tooltip
        id={`tooltip-${to.replace(/\//g, "-")}`}
        {...props}
        className="custom-tooltip"
      >
        <div
          style={{
            padding: "8px 12px",
            textAlign: "left",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: 600,
              letterSpacing: "0.3px",
              marginBottom: description ? "4px" : 0,
            }}
          >
            {title}
          </div>
          {description && (
            <div
              style={{
                fontSize: "12px",
                fontWeight: 400,
                opacity: 0.85,
                lineHeight: 1.4,
              }}
            >
              {description}
            </div>
          )}
        </div>
      </Tooltip>
    );

    // If onClick is provided, render a button instead of a Link
    if (onClick) {
      return (
        <div className="menu-item">
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 200, hide: 100 }}
            overlay={renderTooltip}
          >
            <button
              type="button"
              className={clsx(
                "btn btn-icon btn-active-light-primary w-30px h-30px w-md-40px h-md-40px"
              )}
              onClick={onClick}
            >
              <KTIcon iconName={icon} className="fs-1" />
            </button>
          </OverlayTrigger>
        </div>
      );
    }

    return (
      <div className="menu-item">
        <OverlayTrigger
          placement="bottom"
          delay={{ show: 200, hide: 100 }}
          overlay={renderTooltip}
        >
          <Link
            className={clsx(
              "btn btn-icon btn-active-light-primary w-30px h-30px w-md-40px h-md-40px",
              {
                active: checkIsActive(pathname, to),
              }
            )}
            to={to}
          >
            <KTIcon iconName={icon} className="fs-1" />
          </Link>
        </OverlayTrigger>
      </div>
    );
  }

  return (
    <div className="menu-item me-lg-1">
      <Link
        className={clsx("menu-link py-3", {
          "active menu-here": checkIsActive(pathname, to),
        })}
        to={to}
      >
        {hasBullet && (
          <span className="menu-bullet">
            <span className="bullet bullet-dot"></span>
          </span>
        )}

        {icon && (
          <span className="menu-icon">
            <KTIcon iconName={icon} className="fs-2" />
          </span>
        )}

        {fontIcon && (
          <span className="menu-icon">
            <i className={clsx("bi fs-3", fontIcon)}></i>
          </span>
        )}

        <span className="menu-title">{title}</span>

        {hasArrow && <span className="menu-arrow"></span>}
      </Link>
    </div>
  );
};

export { MenuItem };
